<template>
  <DialogsWrapper v-if="!loading"/>
  <Loading v-model:active="loading" loader="dots"/>
  <div>
    <div v-if="testViewData" class="container mt-5 bg-white shadow p-3">
      <!-- Instructions -->
      <div class="p-3">
        <div>
          {{ testViewData.instruction }}
        </div>
      </div>
      <hr>
      <!-- Questions -->
      <div class="p-3">
        <div v-for="gIndex in maxQuestions" :key="gIndex">
          <TextAlternativeQuestion
            v-if="currentQuestion === gIndex"
            :question="testViewData.questions[gIndex-1]"
            :gIndex="gIndex"
            :alternatives="makeAlternatives(testViewData.questions[gIndex-1].choices)"
            :answersForm="answersForm"
            :questionNumber="currentQuestion"
          />
        </div>
        <!-- End -->
        <div class="py-3 buttons-container">
          <button v-if="currentQuestion !== 1" @click="nextQuestion('back')" class="btn white-general-btn mx-2 my-1">{{ $t('candidates.tests.previous') }}</button>
          <button v-if="currentQuestion < maxQuestions" @click="nextQuestion('next')" class="btn white-general-btn mx-2 my-1">{{ $t('candidates.tests.next') }}</button>
          <button v-if="currentQuestion === maxQuestions" @click="sendAnswers(testAnswer.id, testAnswer.test_request_id)" class="btn general-btn mx-2 my-1">{{ $t('candidates.tests.sendAnswers') }}</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, onBeforeMount } from 'vue'
import { storeToRefs } from 'pinia'
import { useRoute } from 'vue-router'
import { useCandidateTestsStore } from '@/stores/candidateTests.store'
import TextAlternativeQuestion from '@/components/tests/TextAlternativeQuestion.vue'
import { createConfirmDialog } from 'vuejs-confirm-dialog'
import ConfirmationModal from '@/components/ConfirmationModal.vue'
import Loading from 'vue-loading-overlay'
import { useNavigationGuard } from '@/use/useNavigationGuard'
import 'vue-loading-overlay/dist/vue-loading.css'

import { useI18n } from 'vue-i18n'

const { t } = useI18n()

const { updateAnswer, toggleTestStatus, fetchTestData } = useCandidateTestsStore()
const { testViewData, testAnswer, loading, testInProgress } = storeToRefs(useCandidateTestsStore())

const maxQuestions = 50
const currentQuestion = ref(1)
const answersForm = ref({})

const confirmSendAnswersDialog = createConfirmDialog(ConfirmationModal)
const route = useRoute()

/*
  Get test questions form and create empty answer.
*/

onBeforeMount(() => {
  fetchTestData(+route.params.id)
})

/*
  Navigation guard
*/

useNavigationGuard(testInProgress, () => {
  const params = {
    test_answer: {
      answer: answersForm.value,
      test_request_id: testAnswer.value?.test_request_id
    }
  }
  toggleTestStatus(false)
  updateAnswer(testAnswer.value?.id, params, true)
})

/*
  Test in progress
*/

onMounted(() => {
  toggleTestStatus(true)
})

/*
  Questions logic
*/

const nextQuestion = (direction) => {
  switch (direction) {
    case 'next':
      currentQuestion.value += 1
      break
    case 'back':
      currentQuestion.value -= 1
      break
  }
}

const makeAlternatives = (choices) => {
  const alternatives = []
  Object.keys(choices).forEach(function (key) {
    const alternative = {
      text: choices[key],
      value: key
    }
    alternatives.push(alternative)
  })
  return alternatives
}

/*
  Send answers
*/

const sendAnswers = async (testAnswerId, testRequestId) => {
  const params = {
    test_answer: {
      answer: answersForm.value,
      test_request_id: testRequestId
    }
  }
  const confirmation = await confirmSendAnswersDialog.reveal({
    icon: 'circle-info',
    title: t('candidates.tests.aboutToSendAnswers'),
    text: t('confirmAction'),
    confirmBtnText: t('candidates.tests.sendAnswers'),
    cancelBtnText: t('candidates.tests.backToTest')
  })
  // On confirm, change test status and update answer
  if (!confirmation.isCanceled) {
    toggleTestStatus(false)
    updateAnswer(testAnswerId, params, true)
  }
}
</script>

<style scoped>
  .buttons-container{
    text-align: end;
  }
  @media (max-width: 1199.98px) {
    .buttons-container{
    text-align: center;
    }
  }
</style>
